import React, { useState } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { J1RightarrowD, E3OneTimeCharge } from '@e3dc-react/icons'
import { makeStyles } from 'tss-react/mui'
import { Stack, Typography } from '@mui/material'
import { ChargingScheduleModel, RecurringChargingScheduleModel } from '../../models'
import { extractChargingScheduleFormData, extractScheduleSummary } from '../../functions'
import { assertAsRecurringSchedule } from 'src/Libs/assertions'
import { Box } from '@mui/system'
import StyledSwitch from '@e3dc-react/shell/Elements/Switch/StyledSwitch'
import { updateChargingSchedule } from '../../api'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { omit } from 'lodash'
import { useQueryClient } from '@tanstack/react-query'
import useActiveInstallationContext from 'src/Libs/installations/hooks/use-active-installation-context'
import VehicleChargingScheduleModal from './VehicleChargingScheduleModal'

const useStyles = makeStyles()(theme => ({
  updateScheduleButton: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    color: theme.palette.grey.grey01,
    cursor: 'pointer',
  },
  disabledScheduleText: {
    opacity: 0.5,
  },
  scheduleCard: {
    backgroundColor: theme.palette.shades.shade04,
    borderColor: theme.palette.shades.shade03,
    borderRadius: theme.shape.borderRadius,
    borderStyle: 'solid',
    borderWidth: 1,
  },
  oneTimeChargingIcon: {
    width: '34px',
    height: '34px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  disabledIcon: {
    color: theme.palette.grey.grey03,
  },
}))

interface Props {
  chargingSchedule: ChargingScheduleModel
  vehicleId?: string
  installationId?: string
  sunmodeActive: boolean
  temporaryScheduleName: string
  chargeLimitMax?: number
}

/**
 *  Component to show a charging schedule summary
 */
const ChargingScheduleCard: React.FC<Props> = ({
  chargingSchedule,
  vehicleId,
  installationId,
  sunmodeActive,
  temporaryScheduleName,
  chargeLimitMax,
}) => {
  const { classes, cx } = useStyles()
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const { installation } = useActiveInstallationContext()
  const { id, name, targetStateOfCharge, schedule } = chargingSchedule
  const hasName = name !== null || temporaryScheduleName !== null
  const scheduleSummary = extractScheduleSummary(schedule, targetStateOfCharge, t, installation?.timezone)
  const isRecurringSchedule = assertAsRecurringSchedule(schedule)
  const isDisabled = isRecurringSchedule ? !schedule.enabled : false
  const scheduleDisabled = isDisabled || !sunmodeActive

  const [isVehicleChargingScheduleModalOpen, setIsVehicleChargingScheduleModalOpen] = useState(false)

  const queryClient = useQueryClient()
  const queryKey = ['vehicle-integration', 'charging-schedules', Number(vehicleId)]

  const handleOpenChargingScheduleModal = (): void => {
    if (!vehicleId || !installationId) return
    setIsVehicleChargingScheduleModalOpen(true)
  }

  const handleCloseChargingScheduleModal = (): void => {
    setIsVehicleChargingScheduleModalOpen(false)
  }

  const updateSchedule = async (state: boolean): Promise<void> => {
    const newSchedule: RecurringChargingScheduleModel = { ...(schedule as RecurringChargingScheduleModel), enabled: state }
    const updatedSchedule = { ...omit(chargingSchedule, ['id', 'temporaryScheduleName']), schedule: newSchedule } as ChargingScheduleModel

    const { hasErrors } = await updateChargingSchedule(Number(vehicleId), Number(installationId), id, updatedSchedule, authFetch)

    if (hasErrors) {
      enqueueErrorStack(t('emobility.vehicle-integration.charging-schedule.update-schedule-failure-message'))
      return
    }
    queryClient.invalidateQueries({ queryKey })
    enqueueSuccessStack(t('emobility.vehicle-integration.charging-schedule.update-schedule-success-message'))
  }

  return (
    <>
      <Box className={classes.scheduleCard} sx={{ px: 4, py: 2, cursor: 'pointer' }} onClick={handleOpenChargingScheduleModal}>
        <Stack direction="row" alignItems="center">
          <Box sx={{ mr: 2 }} onClick={e => e.stopPropagation()}>
            {isRecurringSchedule ? (
              <StyledSwitch color="primary" checked={schedule.enabled} onChange={e => updateSchedule(e.target.checked)} />
            ) : (
              <Box className={classes.oneTimeChargingIcon}>
                <E3OneTimeCharge className={cx({ [classes.disabledIcon]: scheduleDisabled })} />
              </Box>
            )}
          </Box>
          <Stack>
            {hasName && (
              <Typography className={scheduleDisabled ? classes.disabledScheduleText : ''} variant="h3">
                {name ?? temporaryScheduleName}
              </Typography>
            )}
            <Typography className={scheduleDisabled ? classes.disabledScheduleText : ''} variant="body2">
              {scheduleSummary}
            </Typography>
          </Stack>
          <J1RightarrowD sx={{ ml: 'auto', mr: 0 }} className={classes.updateScheduleButton} />
        </Stack>
      </Box>
      {isVehicleChargingScheduleModalOpen && (
        <VehicleChargingScheduleModal
          isDialogOpen={isVehicleChargingScheduleModalOpen}
          vehicleId={Number(vehicleId)}
          installationId={Number(installationId)}
          scheduleId={id}
          chargingScheduleData={extractChargingScheduleFormData(name, targetStateOfCharge, schedule, installation?.timezone)}
          temporaryScheduleName={temporaryScheduleName}
          isEditing={true}
          chargeLimitMax={chargeLimitMax}
          onClose={handleCloseChargingScheduleModal}
        />
      )}
    </>
  )
}

export default ChargingScheduleCard
